import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { TooltipProvider } from '../../Components/UI/tooltip';
import useIsMobile from '../../Hooks/useIsMobile';
import externalAPI from '../../Connect/ExternalAPI';
import { setGlobalLoading } from '../../Store/Actions';
import { Users, Phone } from 'lucide-react';
import { BsFunnel } from 'react-icons/bs';

const MISVitals = ({ customerData: initialData, onClose }) => {
	const isMobile = useIsMobile();
	const [searchParams] = useSearchParams();
	const [customerData, setCustomerData] = useState(initialData || null);
	const [isLoading, setIsLoading] = useState(!initialData);
	const dispatch = useDispatch();

	useEffect(() => {
		if (initialData) {
			setCustomerData(initialData);
			setIsLoading(false);
			return;
		}

		const agnID = searchParams.get('agnID');
		if (!agnID) {
			setIsLoading(false);
			return;
		}

		const fetchVitalsData = async () => {
			setIsLoading(true);
			dispatch(setGlobalLoading(true));
			try {
				const response = await externalAPI.getAgencyVitals(parseInt(agnID, 10));
				let updtPkg = response?.updatePackage;

				if (typeof updtPkg === 'string') {
					updtPkg = JSON.parse(updtPkg);
				}

				if (updtPkg && updtPkg.Table1 && updtPkg.Table1.length > 0) {
					const vitalsData = updtPkg.Table1[0];
					// Map PascalCase properties to camelCase for ContactActions component
					const mappedData = {
						...vitalsData,
					};
					setCustomerData(mappedData);
				} else {
					setCustomerData(null);
				}
			} catch (error) {
				console.error('Error fetching vitals data:', error);
				setCustomerData(null);
			} finally {
				setIsLoading(false);
				dispatch(setGlobalLoading(false));
			}
		};

		fetchVitalsData();
	}, [searchParams, dispatch, initialData]);

	if (isLoading) {
		return null;
	}

	if (!customerData) {
		return null;
	}

	return (
		<TooltipProvider>
			<div className={`${isMobile ? 'min-h-screen bg-bg-grey p-4 pt-20' : 'min-h-screen bg-bg-grey flex items-center justify-center p-4'}`}>
				<div className={`${isMobile ? 'w-full' : 'w-full max-w-md'} bg-white rounded-lg shadow-md border border-gray-200`}>
					{/* Content */}
					<div className="p-3 px-4 space-y-6">
						{/* Agency Info Section */}
						<div className="space-y-2">
							<h3 className="text-base sm:text-lg font-semibold text-gray-900">
								{customerData?.AgencyName || 'Agency Details'}
							</h3>
							{customerData?.AdminUserName && (
								<div className="flex flex-row justify-between gap-3">
									<p className="text-xs sm:text-sm text-gray-600">
										Contact: {customerData.AdminUserName}
									</p>
								</div>
							)}
						</div>

						{/* Statistics Grid */}
						<div className={isMobile ? "flex flex-col gap-3" : "grid grid-cols-3 gap-2 sm:gap-3"}>
							{/* Users Count */}
							<div className={isMobile ? "bg-gray-100 rounded-lg p-3 border border-gray-200 flex items-center gap-3" : "bg-gray-100 rounded-lg p-3 border border-gray-200 text-center"}>
								{isMobile && <Users size={24} className="text-gray-700 flex-shrink-0" />}
								<div className={isMobile ? "w-full flex items-center flex-row justify-between" : ""}>
									<div className={isMobile ? "text-sm text-gray-600 font-medium" : "text-xs text-gray-600 font-medium mb-1"}>Users</div>
									<div className={isMobile ? "text-xl font-bold text-gray-900" : "text-lg sm:text-2xl font-bold text-gray-900"}>
										{customerData?.CntUsers || 0}
									</div>
								</div>
							</div>

							{/* Contacts Count */}
							<div className={isMobile ? "bg-gray-100 rounded-lg p-3 border border-gray-200 flex items-center gap-3" : "bg-gray-100 rounded-lg p-3 border border-gray-200 text-center"}>
								{isMobile && <Phone size={24} className="text-gray-700 flex-shrink-0" />}
								<div className={isMobile ? "w-full flex items-center flex-row justify-between" : ""}>
									<div className={isMobile ? "text-sm text-gray-600 font-medium" : "text-xs text-gray-600 font-medium mb-1"}>Contacts</div>
									<div className={isMobile ? "text-xl font-bold text-gray-900" : "text-lg sm:text-2xl font-bold text-gray-900"}>
										{customerData?.CntConns || 0}
									</div>
								</div>
							</div>

							{/* Inquiries Count */}
							<div className={isMobile ? "bg-gray-100 rounded-lg p-3 border border-gray-200 flex items-center gap-3" : "bg-gray-100 rounded-lg p-3 border border-gray-200 text-center"}>
								{isMobile && <BsFunnel size={24} className="text-gray-700 flex-shrink-0" />}
								<div className={isMobile ? "w-full flex items-center flex-row justify-between" : ""}>
									<div className={isMobile ? "text-sm text-gray-600 font-medium" : "text-xs text-gray-600 font-medium mb-1"}>Inquiries</div>
									<div className={isMobile ? "text-xl font-bold text-gray-900" : "text-lg sm:text-2xl font-bold text-gray-900"}>
										{customerData?.CntThreads || 0}
									</div>
								</div>
							</div>
						</div>
						<div className={`text-center pb-1 ${isMobile ? "mt-8" : "mt-4"}`}>
							<p className="text-sm text-gray-500">
								Powered by{' '}
								<a href="https://sahicrm.com/" className="text-sahi-green hover:underline">
									SahiClub.com
								</a>
							</p>
						</div>
					</div>
				</div>
			</div>
		</TooltipProvider>
	);
};

export default MISVitals;
